import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import i18n from '@pureartisan/simple-i18n'
import shallow from 'zustand/shallow'
import VariabilityWarning from '../../VariabilityWarning'
import useStore from './../../store'
import {
  getIdentifierPhrase,
  getNormPhrase,
  getStateNameFromFips,
  getMetroNameFromFips,
  getVaribilityLevel,
  getSortedGrades,
  isWkkfView,
} from './../../utils'
import { Typography, Box } from '@material-ui/core'
import IndexWithRating from '../../IndexWithRating/IndexWithRating'
import { theme } from '../../theme'
import _ from 'lodash'
import { COI_METRIC } from '../../../../../constants/map'

const useStyles = makeStyles(theme => ({
  root: {
    width: `${theme.extras.mapPopup.width}px`,
    zIndex: '30',
    '& .loc-name, .comp-to': {
      paddingTop: theme.spacing(0.5),
    },

    '& .red-grades': {
      paddingBottom: theme.spacing(1),
    },

    '& .variability-warning': {
      marginLeft: 'auto',
      verticalAlign: 'bottom',
    },
    '& .click-prompt, .no-data': {
      fontStyle: 'italic',
    },
  },
  section: {
    position: 'relative',
    padding: theme.spacing(2),
    '&:not(:first-child)': {
      borderTop: theme.extras.styles.faintBorder,
    },
    '& h1': {
      display: 'flex',
    },
  },
  redliningPie: {
    width: '40px',
    height: '40px',
    position: 'absolute',
    right: theme.spacing(3),
    top: theme.spacing(2),
    borderRadius: '50%',
  },
}))

const PopupContent = ({ ...props }) => {
  const feature = props.feature
  const {
    activeMetric,
    activeNorm,
    activeGeoType,
    activeView,
    activeRatingType,
    hoveredFeatureId,
    remoteJson,
    redliningActive,
  } = useStore(
    state => ({
      activeMetric: state.activeMetric,
      activeNorm: state.activeNorm,
      activeGeoType: state.activeGeoType,
      activeView: state.activeView,
      activeRatingType: state.activeRatingType,
      hoveredFeatureId: state.hoveredFeatureId,
      remoteJson: state.remoteJson,
      redliningActive: state.redliningActive,
    }),
    shallow,
  )

  const classes = useStyles()

  // If no feature or tract data, return.
  if (!hoveredFeatureId) {
    return ''
  }

  // console.log('M:', feature.properties.m)
  const title =
    feature.properties.m && feature.properties.m !== 0
      ? getMetroNameFromFips(feature.properties.m)
      : feature.properties.s && feature.properties.s !== 0
      ? getStateNameFromFips(feature.properties.s)
      : ''

  const isTract = activeGeoType === 'tracts'

  const variabilityLevel =
    !isTract &&
    getVaribilityLevel({
      metric: activeMetric,
      activeNorm,
      feature,
    })

  const redliningData =
    !!redliningActive &&
    !!isTract &&
    remoteJson.redliningBreakdown.data[hoveredFeatureId]

  const getPercent = n => `${n * 100}%`
  const getRedliningPie = () => {
    if (!redliningData) return null
    const sortedGrades = getSortedGrades(redliningData)
    // console.log({ sortedGrades })
    let colorString = ''
    let runningTotalRatio = 0
    sortedGrades.forEach(({ color, ratio }) => {
      colorString += `${color} ${getPercent(
        runningTotalRatio,
      )}`
      runningTotalRatio += ratio
      colorString += `${getPercent(runningTotalRatio)}, `
    })
    const background = `conic-gradient(${colorString}${
      theme.extras.variables.colors.ungradedGray
    } ${getPercent(runningTotalRatio)})`
    return (
      <Box
        className={classes.redliningPie}
        style={{ background }}
      ></Box>
    )
  }

  const key = `${activeRatingType}${activeMetric}${activeNorm}`
  const rating = feature.properties[key]

  // TODO: abstract away method in util
  const { domain_name } =
    activeMetric === COI_METRIC
      ? remoteJson.indicatorMap.data
      : remoteJson.indicatorMap.data.domains.find(
          d => d.key === activeMetric,
        ) || {}

  return (
    <Box className={classes.root}>
      <Box className={classes.section}>
        <Typography variant="h1">
          {getIdentifierPhrase(activeGeoType, feature.id, {
            truncate: true,
          })}
          {!!variabilityLevel && (
            <VariabilityWarning
              variabilityLevel={variabilityLevel}
              activeGeoType={activeGeoType}
            />
          )}
        </Typography>
        {title && (
          <Typography variant="body1" className="loc-name">
            {title}
          </Typography>
        )}
        <Box>
          <Typography
            className="click-prompt"
            variant="caption"
            color="textSecondary"
          >
            {i18n.translate(`POPUP_CLICK_PROMPT`, {
              geoType: i18n.translate(
                `GEO_TYPE_SIMPLE_SINGULAR_${activeGeoType.toUpperCase()}`,
              ),
            })}
          </Typography>
        </Box>
      </Box>
      {!_.isNaN(parseInt(rating)) && (
        <Box className={classes.section}>
          <IndexWithRating
            index={activeMetric}
            name={domain_name}
            level={activeRatingType === 'x' ? rating : null}
            score={activeRatingType === 's' ? rating : null}
          />
          <Typography
            className="comp-to"
            variant="body1"
            color="textSecondary"
          >
            {i18n.translate(`POPUP_COMPARED_TO`, {
              normPhrase: getNormPhrase(activeNorm),
            })}
          </Typography>
        </Box>
      )}
      {redliningData && (
        <Box className={classes.section}>
          <Typography variant="h2" className="red-grades">
            {i18n.translate(`POPUP_REDLINING_COMP`)}
          </Typography>
          {getRedliningPie()}
        </Box>
      )}
    </Box>
  )
}

export default PopupContent
